import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectIsWishlistsLoading, selectWishlists } from "~/features/wishlist/redux/wishlistSelectors";
import { type WishlistModel } from "~/features/wishlist/types/model";
import { addToWishlistThunk } from "~/features/wishlist/redux/wishlistSlice";
import ATWPopupBase, { type ATWPopupBaseProps } from "~/features/wishlist/components/modals/AddToWishlist/ATWPopupBase";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { Permission, userHasAnyPermission } from "~/domains/user/utils/permissions";
import { INVALID_PACKAGE_CODE } from "~/utils/constants";
import { getNetPrice } from "~/domains/product/utils/productDescriptorUtils";
import Spinner from "~/common/components/Spinner/Spinner";
import EditWishlistNamePopup from "~/features/wishlist/components/modals/EditWishlistNamePopup";
import { IconType } from "~/common/components/icon/IconType";
import WishlistDropdown from "~/features/wishlist/components/WishlistDropdown";
import OptimeSolutionsMessage from "~/domains/optime/components/OptimeSolutionsMessage";
import { WISHLIST_PAGE } from "~/utils/url/staticUrls";
import { Trans, useTranslation } from "react-i18next";
import { isKITProduct } from "~/domains/product/types/ProductData";
import { ProductService } from "~/features/addToCart/api/ProductService";

import { Button, Stack } from "~/common/mui";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import { DefaultButtonIcon } from "~/common/components/icon/Icon";
import { Alert } from "~/common/mui/Alert/Alert";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";

export type AddToWishlistTrackingCb = (descriptor: ProductDescriptor) => void;

export interface AddToWishlistWizardProps {
  productCode: string;
  initialQuantity: number;
  initialPriceReference?: string;
  selectedOptionalParts?: {
    productCode: string;
    quantity: number;
  }[];
  closePopup: () => void;
  addTracking?: AddToWishlistTrackingCb;
}

export enum AddToWishlistStep {
  LOADING,
  HAS_WISHLISTS,
  CREATING_WISHLIST,
  SENDING,
  POST_SENDING_SUCCESS,
  POST_SENDING_ERROR,
}

function getInitialStep(
  product: ProductDescriptor,
  isWishlistsLoading: boolean,
  availableWishlists: WishlistModel[],
): AddToWishlistStep {
  if (isWishlistsLoading) {
    return AddToWishlistStep.LOADING;
  }
  if (availableWishlists.length === 0) {
    return AddToWishlistStep.CREATING_WISHLIST;
  }
  return AddToWishlistStep.HAS_WISHLISTS;
}

export const AddToWishlistWizard = (props: AddToWishlistWizardProps) => {
  const [quantity, setQuantity] = useState(props.initialQuantity);
  const [priceReference, setPriceReference] = useState(props.initialPriceReference ?? getInitialPriceReference());
  const product = ProductService.useProductDescriptor({
    productCode: props.productCode,
    priceReference,
    options: { fetch: true },
  });

  const isWishlistsLoading = useSelector(selectIsWishlistsLoading);
  const availableWishlists = useSelector(selectWishlists);

  const [step, setStep] = useState(() => getInitialStep(product, isWishlistsLoading, availableWishlists));
  const { t } = useTranslation();
  const { closePopup, selectedOptionalParts } = props;

  const [selectedWishlistId, setSelectedWishlistId] = useState<string | undefined>();
  const [addedWishlistName, setAddedWishlistName] = useState("");

  const [clicked, setClicked] = useState(false);

  const isMinDesktop = useIsMinDesktopSize();

  const dispatch = useDispatch();
  const addToWishlistCb = async (wishlistName: string, wishlistId: string | null | undefined) => {
    if (props.addTracking) {
      props.addTracking(product);
    }
    let optionalParts;
    if (selectedOptionalParts) {
      optionalParts = selectedOptionalParts;
    } else if (isKITProduct(product)) {
      optionalParts = product.base.optionalParts.map((item) => ({
        productCode: item.product.code,
        quantity: item.quantity * quantity,
      }));
    }
    setStep(AddToWishlistStep.SENDING);
    try {
      await dispatch(
        addToWishlistThunk({
          quantity: quantity,
          wishlistName: wishlistName,
          productCode: product.base.code,
          id: wishlistId ?? undefined,
          priceReference: priceReference,
          optionalParts,
        }),
      );
      setStep(AddToWishlistStep.POST_SENDING_SUCCESS);
      setAddedWishlistName(wishlistName);
    } catch {
      setStep(AddToWishlistStep.POST_SENDING_ERROR);
    }
  };

  const popupData: ATWPopupBaseProps = {
    product,
    onClose: closePopup,
    onQtyChange: setQuantity,
    disabled: true,
    editable: true,
    quantity,
    price: null,
    priceReference,
    onPriceReferenceChange: (priceRef: string | undefined) => setPriceReference(priceRef ?? ""),
    isDesktop: isMinDesktop,
  };

  if (userHasAnyPermission(Permission.ADD_TO_CART, Permission.ADD_TO_QUOTE)) {
    if (product.prices.length) {
      const firstValidPackCode = product.prices[0]?.packCode ?? INVALID_PACKAGE_CODE;
      const price = getNetPrice({
        totalQuantity: quantity,
        product: product,
        packCode: firstValidPackCode,
      });
      popupData.price = price ?? null;
    }
  }

  if (step === AddToWishlistStep.LOADING || step === AddToWishlistStep.SENDING) {
    if (step === AddToWishlistStep.SENDING) {
      popupData.editable = false;
    }
    return (
      <ATWPopupBase {...popupData}>
        <Spinner className={"mt-2"} />
      </ATWPopupBase>
    );
  }

  popupData.disabled = false;

  if (step === AddToWishlistStep.CREATING_WISHLIST) {
    return <EditWishlistNamePopup onOk={(name) => addToWishlistCb(name, null)} onCancel={closePopup} />;
  }

  if (step === AddToWishlistStep.HAS_WISHLISTS) {
    return (
      <ATWPopupBase
        {...popupData}
        footerChildren={
          <Stack direction={"row"} gap={1}>
            <Button data-test-id={"add-to-wishlist-btn-back"} variant={"text"} onClick={closePopup}>
              {t("addToWishlist.back")}
            </Button>
            <Button
              data-test-id={"add-to-wishlist-btn-add"}
              onClick={() => {
                setClicked(true);
                if (selectedWishlistId) {
                  const wishlistName = availableWishlists.find((wishlist) => wishlist.id === selectedWishlistId)!.name;
                  addToWishlistCb(wishlistName, selectedWishlistId);
                }
              }}
              startIcon={<DefaultButtonIcon type={IconType.Bookmark} />}
            >
              {t("addToWishlist.add")}
            </Button>
          </Stack>
        }
      >
        <WishlistDropdown
          wishlists={availableWishlists}
          onWishlistSelect={setSelectedWishlistId}
          onNewWishlist={() => {
            setSelectedWishlistId(undefined);
            setStep(AddToWishlistStep.CREATING_WISHLIST);
          }}
          selectedWishlist={selectedWishlistId}
          error={clicked && !selectedWishlistId}
        />
        {popupData.product.base.isOptime && <OptimeSolutionsMessage className="mt-30 pt-1" />}
      </ATWPopupBase>
    );
  }

  if (step === AddToWishlistStep.POST_SENDING_ERROR || step === AddToWishlistStep.POST_SENDING_SUCCESS) {
    const wishlistName = addedWishlistName;
    const isNewWishlist = !selectedWishlistId;
    return (
      <ATWPopupBase
        {...popupData}
        editable={false}
        footerChildren={
          <Stack direction={"row"} gap={1}>
            <Button data-test-id={"add-to-wishlist-btn-back"} variant={"text"} onClick={closePopup}>
              {t("addToWishlist.back")}
            </Button>
            <Button
              data-test-id={"add-to-wishlist-btn-show"}
              href={WISHLIST_PAGE}
              startIcon={<DefaultButtonIcon type={IconType.Bookmark} />}
            >
              {t("addToWishlist.show")}
            </Button>
          </Stack>
        }
      >
        <Alert severity={"info"}>
          <Trans i18nKey={`addToWishlist.success.${isNewWishlist ? "new" : "existing"}`}>
            Product has been added to wish list "<b>{{ name: wishlistName }}</b>"
          </Trans>
        </Alert>
        {popupData.product.base.isOptime && <OptimeSolutionsMessage className="mt-2" />}
      </ATWPopupBase>
    );
  }

  return null;
};
